import { ReactElement, useCallback } from 'react';
import { List, Collapse } from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import classNames from 'classnames';

import { useToggle, useCurrentCapacityTypeInfo } from '~hooks/index';
import { StatisticsProgress } from '~components/index';
import { CapacityTypeBase } from '~services/capacityTypes/types';
import {
  parseCapacityValueWithSuffix,
  getDescriptionCapacity,
} from '~utils/capacityTypes';

import { ChargeStatusMapLegendProps, ChargeStatusMapLegendUnit } from './types';
import {
  classes,
  ChargeStatusMapLegendContainer,
  ChargeStatusMapLegendListButton,
  ChargeStatusMapLegendListItemText,
  ChargeStatusMapLegendContent,
  ChargeStatusMapLegendProgressLabelContainer,
  ChargeStatusMapLegendProgressLabelTextSmall,
} from './styles';

const ChargeStatusMapLegend = ({
  unit1,
  unit2,
}: ChargeStatusMapLegendProps): ReactElement => {
  const [expanded, toogleExpanded] = useToggle(true);

  const { unit1CapacityTypeId, unit2CapacityTypeId } =
    useCurrentCapacityTypeInfo();

  const getLabel = useCallback(
    ({ busy, capacity }: ChargeStatusMapLegendUnit, type: CapacityTypeBase) => (
      <ChargeStatusMapLegendProgressLabelContainer>
        <ChargeStatusMapLegendProgressLabelTextSmall fontWeight="bold">
          {getDescriptionCapacity(type)}
        </ChargeStatusMapLegendProgressLabelTextSmall>

        <ChargeStatusMapLegendProgressLabelTextSmall>
          {parseCapacityValueWithSuffix(`${busy} / ${capacity}`, type)}
        </ChargeStatusMapLegendProgressLabelTextSmall>
      </ChargeStatusMapLegendProgressLabelContainer>
    ),
    [],
  );

  const parseLegendPercentage = useCallback(
    (_val: number, _max: number, currentProgressVal: number) =>
      `${currentProgressVal >= 100 ? 100 : currentProgressVal}%`,
    [],
  );

  const getColorByUnit = useCallback(
    ({ busy, capacity }: ChargeStatusMapLegendUnit) =>
      busy > capacity ? 'error' : 'primary',
    [],
  );

  return (
    <ChargeStatusMapLegendContainer>
      <List disablePadding>
        <ChargeStatusMapLegendListButton
          onClick={() => toogleExpanded()}
          className={classNames({ [classes.expanded]: expanded })}
        >
          <ChargeStatusMapLegendListItemText
            primary="Estado de carga"
            primaryTypographyProps={{ variant: 'caption', fontWeight: 'bold' }}
          />

          <KeyboardArrowDown className={classes.expandIcon} />
        </ChargeStatusMapLegendListButton>

        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <ChargeStatusMapLegendContent>
            <StatisticsProgress
              max={unit1.capacity}
              val={unit1.busy}
              size="xSmall"
              themeColor={getColorByUnit(unit1)}
              label={getLabel(unit1, unit1CapacityTypeId)}
              parseLegend={parseLegendPercentage}
            />

            <StatisticsProgress
              max={unit2.capacity}
              val={unit2.busy}
              size="xSmall"
              themeColor={getColorByUnit(unit2)}
              label={getLabel(unit2, unit2CapacityTypeId)}
              parseLegend={parseLegendPercentage}
            />
          </ChargeStatusMapLegendContent>
        </Collapse>
      </List>
    </ChargeStatusMapLegendContainer>
  );
};

export default ChargeStatusMapLegend;
